import { HttpError, useOne, useParse, useUpdate } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { FieldObjectType, IAttendantTemplateInputItem, IAttractionParticipantDetail, IAttractionParticipantForm } from "interfaces";
import { EditInformationSection, EditRegistrationInformationSection } from "./components";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { Link, useNavigate } from "react-router-dom";
import { get } from "lodash";
import toast from "react-hot-toast";
import { ParticipantFormSchema } from "components/attendantTemplate/schema";

const InformationSchema = z.object({
  note: z.string().optional(),
  attendant_template_id: z.string().min(1, "This field is required"),
  category_en: z.string().min(1, "This field is required"),
  is_active: z.boolean(),
})

export const EditAttractionParticipant: React.FC = () => {
  const parse = useParse();
  const { params } = parse();

  const {
    data,
    isFetching,
  } = useOne<IAttractionParticipantDetail>({
    resource: "participant/attractions",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const { data: groupData, isFetching: isFetchingGroups } = useOne<any[], HttpError>({
    resource: "content/dropdown/clubs?dict_response=true",
    id: "",
    meta: {
      resource: "data"
    },
  });

  const participantDetailData = data?.data ?? null;
  const groups = groupData?.data ?? null;

  if (isFetching || isFetchingGroups) {
    return <FullScreenLoading />;
  }
  return participantDetailData ? <EditAttractionParticipantForm participantDetailData={participantDetailData} groups={groups} /> : <></>;
}

const EditAttractionParticipantForm = ({participantDetailData, groups}: {participantDetailData: IAttractionParticipantDetail, groups: any}) => {
  const { mutate, isLoading: isUpdating } = useUpdate();
  const navigate = useNavigate();

  const methods = useForm<IAttractionParticipantForm, HttpError, IAttractionParticipantForm>({
    defaultValues: {
      attendant_template_id: participantDetailData?.participant.attendant_template_id ?? "",
      category_en: participantDetailData.participant.category_en ?? "",
      is_active: participantDetailData.participant.is_active ?? false,
      note: participantDetailData.participant.note ?? ""
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver: any = zodResolver(
        z.object({
          ...InformationSchema.shape,
          ...ParticipantFormSchema(attendantTemplate, get(values, "dob", null), get(participantDetailData, "event.start_date", "") || "").shape
        })
        // ParticipantFormSchema(attendantTemplate, get(values, "dob", null), get(participantDetailData, "event.start_date", "") || "")
      );
      return createResolver(values, context, options);
    },
  });
  
  const { data: templateData, isLoading: isFetchingTemplate } = useOne<any[], HttpError>({
    resource: `participant/events/${participantDetailData?.event?.event_id}/attendant-templates/${methods.watch("attendant_template_id")}`,
    id: "",
    meta: {
      resource: "data"
    },
    queryOptions: {
      enabled: !!(methods.watch("attendant_template_id") && participantDetailData?.event?.event_id),
      select: (response: any) => {
        const formattedData: any = {};

        for (let groupKey in response.data) {
          const group = response.data[groupKey];
          formattedData[groupKey] = {
            ...group,
            fields: group.fields.map((field: IAttendantTemplateInputItem) => 
              field.object === FieldObjectType.CLUBS
                ? { ...field, option: groups }
                : field
            )
          };
        }

        return {
          ...response,
          data: {
            ...formattedData,
          },
        };
      },
      
    } as any
  });

  const attendantTemplate = templateData?.data ?? {};

  const onSubmit = (data: IAttractionParticipantForm) => {
    const formattedData = {
      ...data,
      first_name: data?.first_name ? data.first_name : null,
      last_name: data?.last_name ? data.last_name : null,
    }

    mutate(
      {
        resource: "participant/attractions",
        id: participantDetailData.participant.id,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...formattedData,
        },
      },
      {
        onSuccess: () => {
          toast.success("Edit successfully");
          navigate(`/participants/attraction/${participantDetailData.participant.id}`);
        },
      }
    );
  }

  const onError = (errors: any) => {
    console.log(errors);
  };
  
  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit, onError)} className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4 h-full">
          <Breadcrumbs customPageLabel="Edit Attraction participant" />
          {participantDetailData ? (
            <>
              <EditInformationSection participantDetailData={participantDetailData} />
              <EditRegistrationInformationSection participantDetailData={participantDetailData} template={attendantTemplate} />
            </>
          ) : <></>}
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto" style={{boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px"}}>
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/participants/attraction/${participantDetailData?.participant.id}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit">
                Save
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
      {isFetchingTemplate || isUpdating ? <FullScreenLoading /> : <></>}
    </div>
  );
}